"use client";

import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useCreateOpportunity } from "@/services/opportunities/queries";
import { useAuthStore } from "@/store/auth";
import { showToast } from "@/components/ui/show-toast";
import { useUploadFileMutation } from "@/services/files/queries";
import { StepOneForm } from "./step-one-form";
import { StepTwoForm } from "./step-two-form";
import type { CustomQuestion, SettingsData } from "@/services/types";
import { usePostHog } from "posthog-js/react";
// import type { , SettingsData } from "./types";

// Schema for step 1 form validation
const opportunitySchema = z
  .object({
    title: z
      .string()
      .min(1, "Opportunity title is required")
      .min(3, "Title must be at least 3 characters")
      .max(100, "Title must be less than 100 characters"),
    summary: z
      .string()
      .min(1, "Summary is required")
      .min(10, "Summary must be at least 10 characters")
      .max(1000, "Summary must be less than 1000 characters"),
    media: z.string().url("Invalid URL").min(1, "Media is required"),
    isExternal: z.boolean(),
    url: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    if (data.isExternal && !data.url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["url"],
        message: "URL is required for external opportunities",
      });
    }
  });

type OpportunityFormData = z.infer<typeof opportunitySchema>;

// Default question names that should not be submitted
const DEFAULT_QUESTION_NAMES = ["name", "email", "phone_number"];

// Default custom questions
const getDefaultCustomQuestions = (): CustomQuestion[] => [
  {
    name: "name",
    label: "Contact Person Name",
    type: "text",
    required: true,
    order: 1,
    placeholder: "Enter your full name",
    textConfig: { maxLength: 50 },
  },
  {
    name: "email",
    label: "Email",
    type: "text",
    required: true,
    order: 2,
    placeholder: "Enter your email address",
    textConfig: { maxLength: 50 },
  },
  {
    name: "phone_number",
    label: "Phone Number",
    type: "number",
    required: true,
    order: 3,
    placeholder: "Enter your phone number",
    numberConfig: { min: 5, max: 11 },
  },
];

export function AddOpportunityForm() {
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string>("");
  const [mediaUrl, setMediaUrl] = useState<string>("");
  const [mediaName, setMediaName] = useState<string>("");
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>(
    getDefaultCustomQuestions()
  );
  const [settings, setSettings] = useState<SettingsData>({
    submissionDeadline: "",
    confirmationEmail:
      "Thank you for applying! We'll review your application and get back to you within 2 weeks.",
    notificationEmails: [],
  });
  const [status, setStatus] = useState<string>("published");
  const router = useNavigate();
  const posthog = usePostHog();
  const user = useAuthStore((s) => s.user);
  const activeOrganisation = user?.organisation?.id || "";
  const activeWorkspace = useAuthStore((s) => s.activeWorkspace);
  const { mutate: createOpportunity, isPending: isCreating } =
    useCreateOpportunity(
      () => {
        posthog?.capture('form_submitted', { form_name: 'add_opportunity', success: true });
        router("/dashboard/opportunities");
      },
      (error: any) => {
        posthog?.capture('form_submitted', { form_name: 'add_opportunity', success: false });
        console.error(error?.response?.data || error);
        showToast(
          "error",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create opportunity. Please try again."
        );
      }
    );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<OpportunityFormData>({
    resolver: zodResolver(opportunitySchema),
    mode: "onBlur",
    defaultValues: {
      isExternal: false,
    },
  });

  const buildOpportunityPayload = () => {
    const data = watch();
    return {
      title: data.title,
      summary: data.summary,
      url: data.isExternal ? data.url : undefined,
      ...(mediaUrl && {
        media: {
          url: mediaUrl,
          name: mediaName || "opportunity media",
        },
      }),
      workspace: activeWorkspace || "",
      customQuestions: customQuestions
        .filter((q) => !DEFAULT_QUESTION_NAMES.includes(q.name))
        .map((q, idx) => {
        const isSelectType =
          q.type === "singleselect" || q.type === "multiselect";
        const questionData: any = {
          name: q.name,
          label: q.label,
          type: q.type,
          required: q.required,
          order: idx + 1,
        };

        // Only include options if it's a select type
        if (isSelectType && q.options && q.options.length > 0) {
          questionData.options = q.options;
        }

        // Include optional fields if they exist
        if (q.placeholder) questionData.placeholder = q.placeholder;
        if (q.helpText) questionData.helpText = q.helpText;

        // Include type-specific configs (required by API)
        if (q.type === "file") {
          questionData.fileConfig = q.fileConfig || {
            maxFiles: 1,
            allowedFormats: [],
          };
        }
        if (q.type === "number") {
          questionData.numberConfig = q.numberConfig || {
            min: 0,
            max: 0,
          };
        }
        if (q.type === "text") {
          questionData.textConfig = q.textConfig || {
            maxLength: 50,
          };
        }
        if (q.type === "textarea") {
          questionData.textConfig = q.textConfig || {
            maxLength: 1000,
          };
        }

        return questionData;
      }),
      settings: {
        submissionDeadline: settings.submissionDeadline,
        confirmationEmail: settings.confirmationEmail,
        notificationEmails: settings.notificationEmails,
      },
      status,
    };
  };

  const { mutateAsync: uploadFileAsync } = useUploadFileMutation(
    () => {
      // Success handled in handleMediaChange
    },
    (error: any) => {
      showToast("error", "Failed to upload media file. Please try again.");
      console.error("Upload error:", error);
    }
  );

  const MAX_COVER_SIZE_BYTES = 3 * 1024 * 1024;

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("error", "Please upload an image file.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_COVER_SIZE_BYTES) {
      showToast("error", "Cover image must be 3MB or smaller.");
      e.target.value = "";
      return;
    }

    setIsUploadingMedia(true);
    setMediaFile(file);

    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    try {
      const response = await uploadFileAsync({
        file,
        name: file.name,
        owner: activeOrganisation
          ? {
              id: activeOrganisation,
              resourceType: "Organisation",
            }
          : undefined,
      });

      if (response?.url) {
        setMediaUrl(response.url);
        setMediaName(response.name || file.name);
        setValue("media", response.url, { shouldValidate: true });
        showToast("success", "Media file uploaded successfully");
      } else {
        throw new Error("No URL returned from upload");
      }
    } catch (error) {
      setMediaFile(null);
      setMediaPreview("");
      setMediaUrl("");
      setMediaName("");
      setValue("media", "", { shouldValidate: true });
      e.target.value = "";
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview("");
    setMediaUrl("");
    setMediaName("");
    setValue("media", "");
  };

  const handleClearUrl = () => {
    setValue("url", "");
  };

  const onSubmit = (_data: OpportunityFormData) => {
    const isExternal = watch("isExternal");
    if (step === 1) {
      if (isExternal) {
        const payload = buildOpportunityPayload();
        createOpportunity(payload as any);
        return;
      }
      setStep(2);
      return;
    }

    // Validate that there is at least one custom question
    if (customQuestions.length === 0) {
      showToast("error", "At least one custom question is required");
      return;
    }

    // Validate that select type questions have options
    const invalidQuestions = customQuestions.filter(
      (q) =>
        (q.type === "singleselect" || q.type === "multiselect") &&
        (!q.options || q.options.length === 0)
    );

    if (invalidQuestions.length > 0) {
      showToast("error", "Select type questions must have at least one option");
      return;
    }

    // Media is already uploaded, create opportunity with the URL
    const payload = buildOpportunityPayload();
    createOpportunity(payload as any);
  };

  function handleBack() {
    if (step === 2) {
      setStep(1);
    } else {
      router(-1);
    }
  }

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold">
          {step === 1 ? "Add New Opportunity" : "Custom Questions & Settings"}
        </h1>
        <p className="mt-2 text-gray-600">
          {step === 1
            ? "Provide all details to create a new opportunity."
            : "Configure custom questions for applicants and notification settings."}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {step === 1 && (
          <StepOneForm
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            mediaFile={mediaFile}
            mediaPreview={mediaPreview}
            onMediaChange={handleMediaChange}
            onRemoveMedia={handleRemoveMedia}
            onClearUrl={handleClearUrl}
            isUploadingMedia={isUploadingMedia}
            settings={settings}
            setSettings={setSettings}
            status={status}
            setStatus={setStatus}
          />
        )}

        {step === 2 && (
          <StepTwoForm
            customQuestions={customQuestions}
            setCustomQuestions={setCustomQuestions}
            settings={settings}
            setSettings={setSettings}
            status={status}
            setStatus={setStatus}
            isExternal={watch("isExternal")}
          />
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-6">
          <Button
            onClick={handleBack}
            type="button"
            variant="outline"
            className="flex-1 h-11 text-base font-semibold bg-transparent"
            trackingEvent='opportunity_form_back'
            trackingProperties={{ step }}
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={isCreating || isUploadingMedia}
            className="flex-1 h-11 text-base font-semibold bg-primary hover:opacity-90"
            trackingEvent={step === 1 ? 'opportunity_form_next' : 'opportunity_form_submit'}
            trackingProperties={{ step }}
          >
            {step === 1
              ? watch("isExternal")
                ? isCreating || isUploadingMedia
                  ? "Submitting..."
                  : "Submit"
                : "Next"
              : isCreating || isUploadingMedia
                ? "Creating..."
                : "Create Opportunity"}
          </Button>
        </div>
      </form>
    </>
  );
}
